import React from 'react';
import PropTypes from 'prop-types';
import { Card, Row, Col, Form, Input, Select } from 'antd';
import RichTextSection from '../../RichTextSection';
import styles from './CompactFormView.module.css';

/**
 * CompactFormView - Direct edit mode with 2-column layout
 *
 * Extracted from ReportWriterV2 to improve modularity
 */
const CompactFormView = ({ selectedReport, formData, onFormDataChange }) => {
  const cardClass = selectedReport === 'knowledge_gap' ? styles.cardKnowledgeGap : styles.cardKeyDecision;
  const bottomCardClass = selectedReport === 'knowledge_gap' ? styles.bottomCardKnowledgeGap : styles.bottomCardKeyDecision;

  const updateFormData = (updates) => {
    onFormDataChange({ ...formData, ...updates });
  };

  // Character limits for each section
  const charLimits = {
    knowledge_gap: {
      description: 125,
      purpose: 300,
      what_we_have_done: 850,
      what_we_have_learned: 1200,
      recommendations: 300
    },
    key_decision: {
      description: 125,
      purpose: 300,
      what_we_have_done: 850,
      what_we_have_learned: 900,
      recommendations: 600
    }
  };

  const limits = charLimits[selectedReport];

  return (
    <div className={styles.container}>
      {/* Top Row: Report Info + Project Details (mirrors PowerPoint: 46.5% / 52.5%) */}
      <Row gutter={16} className={styles.topRow}>
        {/* Left Header Box - 46.5% width */}
        <Col span={11}>
          <Card
            size="small"
            className={cardClass}
          >
            <Form.Item label="Title" className={styles.formItemSmallMargin}>
              <Input
                value={formData.title}
                onChange={(e) => updateFormData({ title: e.target.value })}
                placeholder="Enter title"
              />
            </Form.Item>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item label="Owner" className={styles.formItemSmallMargin}>
                  <Input
                    value={formData.owner}
                    onChange={(e) => updateFormData({ owner: e.target.value })}
                    placeholder="Owner"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Learning Cycle" className={styles.formItemSmallMargin}>
                  <Input
                    value={formData.learning_cycle || ''}
                    onChange={(e) => updateFormData({ learning_cycle: e.target.value })}
                    placeholder="Enter learning cycle"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label={selectedReport === 'key_decision' ? 'Decision Maker' : 'Contributors'} className={styles.formItemNoMargin}>
              <Input
                value={selectedReport === 'key_decision' ? formData.decision_maker : formData.contributors}
                onChange={(e) => updateFormData(selectedReport === 'key_decision' ? { decision_maker: e.target.value } : { contributors: e.target.value })}
                placeholder={selectedReport === 'key_decision' ? 'Decision Maker' : 'Contributors'}
              />
            </Form.Item>
          </Card>
        </Col>

        {/* Right Header Box - 52.5% width */}
        <Col span={13}>
          <Card
            size="small"
            className={cardClass}
          >
            <Form.Item label="Project Name" className={styles.formItemSmallMargin}>
              <Input
                value={formData.project_name}
                onChange={(e) => updateFormData({ project_name: e.target.value })}
                placeholder="Project name"
              />
            </Form.Item>
            <Form.Item label={selectedReport === 'knowledge_gap' ? 'Knowledge Gap #' : 'Key Decision #'} className={styles.formItemSmallMargin}>
              <Input
                value={selectedReport === 'knowledge_gap' ? formData.kg_number : formData.kd_number}
                onChange={(e) => updateFormData(selectedReport === 'knowledge_gap' ? { kg_number: e.target.value } : { kd_number: e.target.value })}
                placeholder="#"
              />
            </Form.Item>
            <Form.Item label="Status" className={styles.formItemNoMargin}>
              <Select
                value={formData.status || (selectedReport === 'knowledge_gap' ? 'in_progress' : 'draft')}
                onChange={(value) => updateFormData({ status: value })}
              >
                {selectedReport === 'knowledge_gap' ? (
                  <>
                    <Select.Option value="in_progress">In Progress</Select.Option>
                    <Select.Option value="completed">Completed</Select.Option>
                    <Select.Option value="draft">Draft</Select.Option>
                  </>
                ) : (
                  <>
                    <Select.Option value="draft">Draft</Select.Option>
                    <Select.Option value="in_progress">In Progress</Select.Option>
                    <Select.Option value="resolved">Resolved</Select.Option>
                    <Select.Option value="completed">Completed</Select.Option>
                  </>
                )}
              </Select>
            </Form.Item>
          </Card>
        </Col>
      </Row>

      {/* Bottom Row: Single Card with 2-Column Content Layout (mirrors PowerPoint: 46.5% / 52.5%) */}
      <Card size="small" className={bottomCardClass}>
        <Row gutter={16} className={styles.contentRow}>
          {/* Left Column - 46.5% width (11/24 columns ≈ 45.8%) */}
          <Col span={11} className={styles.column}>
            <div className={styles.columnContent}>
              <RichTextSection
                label={selectedReport === 'knowledge_gap' ? 'The Question to Answer' : 'The Key Decision'}
                value={formData.description}
                onChange={(value) => updateFormData({ description: value })}
                placeholder="Enter description..."
                showTips={false}
                minHeight="80px"
                charLimit={limits.description}
              />

              <RichTextSection
                label="The Purpose"
                value={formData.purpose}
                onChange={(value) => updateFormData({ purpose: value })}
                placeholder="Enter purpose..."
                showTips={false}
                minHeight="110px"
                charLimit={limits.purpose}
              />

              <RichTextSection
                label="What We Have Done"
                value={formData.what_we_have_done}
                onChange={(value) => updateFormData({ what_we_have_done: value })}
                placeholder="Enter what was done..."
                showTips={false}
                minHeight="200px"
                charLimit={limits.what_we_have_done}
              />
            </div>
          </Col>

          {/* Right Column - 52.5% width (13/24 columns ≈ 54.2%) */}
          <Col span={13} className={styles.column}>
            <div className={styles.columnContent}>
              <RichTextSection
                label="What We Have Learned"
                value={formData.what_we_have_learned}
                onChange={(value) => updateFormData({ what_we_have_learned: value })}
                placeholder="Enter learnings..."
                showTips={false}
                showInlineTip={true}
                minHeight={selectedReport === 'knowledge_gap' ? '300px' : '240px'}
                charLimit={limits.what_we_have_learned}
              />

              <RichTextSection
                label={selectedReport === 'knowledge_gap' ? 'Recommendations and Next Steps' : 'What We Recommend / What We Have Decided'}
                value={formData.recommendations}
                onChange={(value) => updateFormData({ recommendations: value })}
                placeholder={selectedReport === 'key_decision' ? 'Enter recommendations and final decision...' : 'Enter recommendations...'}
                showTips={false}
                minHeight="150px"
                charLimit={limits.recommendations}
              />
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

CompactFormView.propTypes = {
  selectedReport: PropTypes.oneOf(['knowledge_gap', 'key_decision']).isRequired,
  formData: PropTypes.shape({
    title: PropTypes.string,
    owner: PropTypes.string,
    decision_maker: PropTypes.string,
    contributors: PropTypes.string,
    project_name: PropTypes.string,
    kg_number: PropTypes.string,
    kd_number: PropTypes.string,
    status: PropTypes.string,
    learning_cycle: PropTypes.string,
    description: PropTypes.string,
    purpose: PropTypes.string,
    what_we_have_done: PropTypes.string,
    what_we_have_learned: PropTypes.string,
    recommendations: PropTypes.string,
  }).isRequired,
  onFormDataChange: PropTypes.func.isRequired,
};

export default CompactFormView;
